import { useState } from "react";
import { useNavigate } from "react-router";
import type { DownloadSummary } from "../downloads/useDownloadSummary";
import { BookmarkEditor } from "../pages/BookmarksPage";
import { useAcks } from "../policy/ack";
import { useServerBlocks } from "../policy/blocked";
import { useSession } from "../session";
import { Dialog } from "../ui/Dialog";
import { Cover, coverOf } from "../ui/GalleryCard";
import { Icon } from "../ui/icons";
import { PanelItem } from "../ui/Sidebar";
import { useLoad } from "../ui/useLoad";
import { VideoLibrary } from "../videos/shared";
import { useFavorites } from "./favorites";

/** What the downloads row says on its right: running first, else waiting, else nothing. */
function downloadsNote(summary: DownloadSummary | null): string | null {
  if (!summary) return null;
  if (summary.mine.running > 0) return `${summary.mine.running}개 받는 중`;
  if (summary.mine.queued > 0) return `대기 ${summary.mine.queued}`;
  return null;
}

/** The global slots and my running jobs' combined progress; no percentage when a total is unknown. */
function DownloadMeter({ summary }: { summary: DownloadSummary }) {
  const percent = summary.progress
    ? Math.round((summary.progress.done / summary.progress.total) * 100)
    : null;
  return (
    <div className="dlmini">
      <p>
        <span>
          전체 {summary.slots.running} / {summary.slots.limit} 슬롯
        </span>
        {percent !== null && <span>{percent}%</span>}
      </p>
      <div className="prog" data-pending={percent === null ? "true" : undefined}>
        {percent !== null && <i style={{ width: `${percent}%` }} />}
      </div>
    </div>
  );
}

/**
 * "내 보관함" of the open sidebar (46 Spec 3.5): history, downloads with their summary, up to 8 of my
 * bookmarks and always "북마크 모두". A bookmark's small picture follows the card's cover rule: no cover address
 * (blocked) or no preview is hatching, an unconfirmed warning is blurred.
 */
export function MyLibrary({
  pathname,
  history,
  downloads,
  summary,
}: {
  pathname: string;
  history: boolean;
  downloads: boolean;
  summary: DownloadSummary | null;
}) {
  const { client } = useSession();
  const favorites = useFavorites();
  const acks = useAcks();
  const blocks = useServerBlocks();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const list = useLoad(
    () => client.call("personal.listBookmarks", { query: { scope: "mine", limit: 8 } }),
    `${favorites.revision}:${favorites.bookmarksRevision}`,
  );
  // keep the rows while a new list is on its way, so the panel does not jump
  const [rows, setRows] = useState(list.data?.items ?? []);
  if (list.data && list.data.items !== rows) setRows(list.data.items);
  const onRow = rows.some((b) => pathname === `/bookmarks/${b.id}`);
  const allCurrent =
    (pathname === "/bookmarks" || pathname.startsWith("/bookmarks/") || pathname === "/favorites") && !onRow;
  const note = downloadsNote(summary);

  return (
    <div className="sg">
      <h2 className="sg-title">
        내 보관함
        <button type="button" className="sg-add" aria-label="북마크 만들기" onClick={() => setCreating(true)}>
          <Icon name="plus" small />
        </button>
      </h2>
      <PanelItem to="/history" icon={<Icon name="clock" />} label="기록" current={history} />
      <PanelItem to="/downloads" icon={<Icon name="down" />} label="다운로드" current={downloads}>
        {note && <span className="n">{note}</span>}
      </PanelItem>
      {summary && summary.mine.running > 0 && <DownloadMeter summary={summary} />}
      <VideoLibrary />
      {rows.map((b) => {
        const first = b.previews[0];
        const cover = first && !blocks.isBlocked(first.gid) ? coverOf(first) : null;
        const veiled = first?.policy.level === "warn" && !acks.isConfirmed(first.gid);
        return (
          <PanelItem
            key={b.id}
            to={`/bookmarks/${b.id}`}
            icon={
              <span
                className={cover === null ? "mini-cover hatch" : "mini-cover"}
                data-testid="bookmark-picture"
              >
                {cover !== null && <Cover key={cover} src={cover} veiled={veiled} />}
              </span>
            }
            label={b.name}
            current={pathname === `/bookmarks/${b.id}`}
          >
            {b.visibility === "public" && (
              <span className="pub" role="img" aria-label="공개">
                <Icon name="globe" small />
              </span>
            )}
            <span className="n">{b.itemCount}</span>
          </PanelItem>
        );
      })}
      <PanelItem to="/bookmarks" icon={<Icon name="bookmark" />} label="북마크 모두" current={allCurrent} />
      {creating && (
        <Dialog label="북마크 만들기" className="modal-small" onClose={() => setCreating(false)}>
          <h2>북마크 만들기</h2>
          <BookmarkEditor
            onCancel={() => setCreating(false)}
            onDone={(b) => {
              setCreating(false);
              favorites.bookmarksChanged();
              navigate(`/bookmarks/${b.id}`);
            }}
          />
        </Dialog>
      )}
    </div>
  );
}
